import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useGetIdentity } from '@refinedev/core';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
    Property,
    PropertyApiRequest,
    PropertyApiResponse,
    PropertyContent,
    PropertyDetailsType,
    PropertyDetailsSize,
    PropertyFAQ,
    PropertyPhoto,
    type PropertyDetails as PropertyDetail
} from '../../interfaces/property';
import { IProfileV1 } from '../../interfaces/user';
import { apiJson, apiVersion } from '../../utils/api';
import { normalizePhotosWithMain } from '../../utils/photos';
import { usePermissions } from '../../hooks';
import { preloadDefaultDetails } from '../../lib/property/preloadDefaultDetails';
import TurndownService from 'turndown';
import { marked } from 'marked';

// Initialize turndown service for HTML to markdown conversion
const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-'
});

const htmlToMarkdown = (html: string): string => {
    if (!html || !html.trim()) return '';
    try {
        return turndownService.turndown(html);
    } catch (error) {
        console.error('Error converting HTML to markdown:', error);
        return '';
    }
};

const markdownToHtml = (markdown: string): string => {
    if (!markdown || !markdown.trim()) return '';
    try {
        return marked.parse(markdown) as string;
    } catch (error) {
        console.error('Error converting markdown to HTML:', error);
        return '';
    }
};

const createDefaultProperty = (): Property => ({
    name: '',
    contact: '',
    address: '',
    coordinates: { lon: -15.71136, lat: 27.896689 },
    photo: undefined,
    photos: [],
    default_language: 'en',
    active: true,
    deleted: false,
    created_date: Date.now(),
    updated_date: Date.now(),
    nano_id: '',
    uid: '',
    website: '',
    content: {
        en: {
            description: '',
            location: '',
            details: preloadDefaultDetails(),
            faq: []
        }
    }
});

export const usePropertyDetailsViewModel = (propertyId: string | undefined) => {
    const navigate = useNavigate();
    const { hasFullAccess, isLoading: isPermissionsLoading } = usePermissions();
    const { data: currentUser } = useGetIdentity<IProfileV1 | null>();
    const MAPBOX_TOKEN = import.meta.env.VITE_APP_MAP_ID;

    // Form state
    const [property, setProperty] = useState<Property>(createDefaultProperty);
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{ place_name: string; center: [number, number] }>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
    const [publishStatus, setPublishStatus] = useState<'idle' | 'publishing' | 'redirecting' | 'success' | 'error'>(
        'idle'
    );
    const [publishUrl, setPublishUrl] = useState<string | null>(null);
    const [publishInvoicePdf, setPublishInvoicePdf] = useState<string | null>(null);
    const [publishUrlModalOpen, setPublishUrlModalOpen] = useState(false);
    const [publishConfirmModalOpen, setPublishConfirmModalOpen] = useState(false);
    const [applyDiscount, setApplyDiscount] = useState(false);
    const [discountPeriod, setDiscountPeriod] = useState<3 | 6 | 12 | null>(null);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [newLanguageDialogOpen, setNewLanguageDialogOpen] = useState(false);
    const [newLanguageCode, setNewLanguageCode] = useState('');
    const [isAnyDragging, setIsAnyDragging] = useState(false);

    // Map refs (these stay in the component as they're DOM-related)
    const map = useRef<mapboxgl.Map | null>(null);
    const marker = useRef<mapboxgl.Marker | null>(null);

    // Server-side ownership UX guard: non-admin users can only view properties
    // they own. The API enforces ownership server-side for additional security.
    useEffect(() => {
        if (
            !isPermissionsLoading &&
            !hasFullAccess &&
            property &&
            property.profile &&
            currentUser &&
            property.profile.id !== currentUser.id
        ) {
            navigate('/properties');
        }
    }, [hasFullAccess, isPermissionsLoading, navigate, property, currentUser]);
    const loadProperty = useCallback(async (id: string) => {
        setIsLoading(true);
        try {
            const data = await apiJson<PropertyApiResponse>(`admin/${apiVersion}/properties/${id}`);
            const loadedProperty = data as Property;
            const normalizedProperty: Property = {
                ...loadedProperty,
                content: Object.fromEntries(
                    Object.entries(loadedProperty.content).map(([lang, langContent]) => [
                        lang,
                        langContent
                    ])
                )
            };
            setProperty(normalizedProperty);
            setSelectedLanguage(normalizedProperty.default_language);
        } catch (error) {
            console.error('Error loading property:', error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load property when ID changes
    useEffect(() => {
        if (propertyId) {
            loadProperty(propertyId);
        }
    }, [propertyId, loadProperty]);

    const handleSave = useCallback(async (profileId?: string) => {
        setSaveStatus('saving');
        console.log('[handleSave] Saving property started...');
        try {
            // Prepare property data for API
            const contentWithMarkdown: { [language: string]: PropertyContent } = {};

            Object.keys(property.content).forEach(lang => {
                const langContent = property.content[lang];
                console.log(`[handleSave] Processing content for language: ${lang}`);

                // Convert HTML to markdown if present (defensive check)
                const description =
                    langContent.description.includes('<') && langContent.description.includes('>')
                        ? htmlToMarkdown(langContent.description)
                        : langContent.description;

                // Ensure details contents are markdown
                const details = langContent.details.map((detail: PropertyDetail, idx: number) => {
                    const isHtml = detail.contents.includes('<') && detail.contents.includes('>');
                    if (isHtml) {
                        console.log(`[handleSave] Converting details[${idx}].contents to markdown (was HTML)`);
                    }
                    const contents = isHtml ? htmlToMarkdown(detail.contents) : detail.contents;
                    return { ...detail, contents };
                });

                // Ensure FAQ answers are markdown
                const faq = langContent.faq.map((item: PropertyFAQ, faqIdx: number) => {
                    const isHtml = item.answer.includes('<') && item.answer.includes('>');
                    if (isHtml) {
                        console.log(`[handleSave] Converting faq[${faqIdx}].answer to markdown (was HTML)`);
                    }
                    const answer = isHtml ? htmlToMarkdown(item.answer) : item.answer;
                    return { ...item, answer };
                });

                contentWithMarkdown[lang] = {
                    description,
                    location: langContent.location,
                    details,
                    faq
                };
            });

            // Prepare request payload
            const requestPayload: PropertyApiRequest = {
                name: property.name,
                contact: property.contact,
                address: property.address,
                coordinates: property.coordinates,
                geolocation: {
                    type: 'Point',
                    coordinates: [property.coordinates.lon, property.coordinates.lat]
                },
                photo: property.photo,
                photos: property.photos,
                default_language: property.default_language,
                active: property.active,
                deleted: property.deleted,
                website: property.website,
                content: contentWithMarkdown,
                ...(property.created_date && { created_date: property.created_date }),
                updated_date: Date.now(),
                ...(property.nano_id && { nano_id: property.nano_id }),
                ...(property.uid && { uid: property.uid }),
                ...(profileId ? { profile_id: profileId } : {})
            };

            console.log('[handleSave] Request payload:', requestPayload);

            // Determine if this is a new property (no uid) or existing (has uid)
            const isNewProperty = !property.uid;
            console.log(`[handleSave] isNewProperty = ${isNewProperty}`);

            const endpoint = isNewProperty
                ? `admin/${apiVersion}/properties`
                : `admin/${apiVersion}/properties/${property.uid}`;
            const method = isNewProperty ? 'POST' : 'PUT';

            console.log(`[handleSave] Making ${method} request to ${endpoint}`);

            const response = isNewProperty
                ? await apiJson<PropertyApiResponse>(endpoint, {
                      method: 'POST',
                      body: JSON.stringify(requestPayload)
                  })
                : await apiJson<PropertyApiResponse>(endpoint, {
                      method: 'PUT',
                      body: JSON.stringify(requestPayload)
                  });

            // Update local state with response
            const savedProperty = response as Property;
            setProperty(savedProperty);
            console.log('[handleSave] Property saved successfully!', savedProperty);

            setSaveStatus('success');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (error) {
            console.error('Error saving property:', error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 5000);
        }
    }, [property, applyDiscount, discountPeriod, currentUser]);

    // Publish property
    const handlePublish = useCallback(async () => {
        if (!property.uid) {
            console.error('[handlePublish] Cannot publish property without uid');
            return;
        }

        // Show confirmation modal instead of directly publishing
        setDiscountPeriod(null);
        setPublishConfirmModalOpen(true);
    }, [property.uid]);

    // Confirm and execute publish
    const handlePublishConfirm = useCallback(async () => {
        setPublishConfirmModalOpen(false);
        if (!property.uid) {
            console.error('[handlePublishConfirm] Cannot publish property without uid');
            return;
        }

        console.log('[handlePublishConfirm] Starting publish for property:', property.uid);
        setPublishStatus('publishing');

        try {
            // Prepare property data for API (same as handleSave)
            const contentWithMarkdown: { [language: string]: PropertyContent } = {};

            Object.keys(property.content).forEach(lang => {
                const langContent = property.content[lang];
                console.log(`[handlePublish] Processing content for language: ${lang}`);

                // Convert HTML to markdown if present (defensive check)
                const description =
                    langContent.description.includes('<') && langContent.description.includes('>')
                        ? htmlToMarkdown(langContent.description)
                        : langContent.description;

                // Ensure details contents are markdown
                const details = langContent.details.map((detail: PropertyDetail, idx: number) => {
                    const isHtml = detail.contents.includes('<') && detail.contents.includes('>');
                    if (isHtml) {
                        console.log(`[handlePublish] Converting details[${idx}].contents to markdown (was HTML)`);
                    }
                    const contents = isHtml ? htmlToMarkdown(detail.contents) : detail.contents;
                    return { ...detail, contents };
                });

                // Ensure FAQ answers are markdown
                const faq = langContent.faq.map((item: PropertyFAQ, faqIdx: number) => {
                    const isHtml = item.answer.includes('<') && item.answer.includes('>');
                    if (isHtml) {
                        console.log(`[handlePublish] Converting faq[${faqIdx}].answer to markdown (was HTML)`);
                    }
                    const answer = isHtml ? htmlToMarkdown(item.answer) : item.answer;
                    return { ...item, answer };
                });

                contentWithMarkdown[lang] = {
                    description,
                    location: langContent.location,
                    details,
                    faq
                };
            });

            // Prepare request payload (same as handleSave)
            const requestPayload: PropertyApiRequest = {
                name: property.name,
                contact: property.contact,
                address: property.address,
                coordinates: property.coordinates,
                geolocation: {
                    type: 'Point',
                    coordinates: [property.coordinates.lon, property.coordinates.lat]
                },
                photo: property.photo,
                photos: property.photos,
                default_language: property.default_language,
                active: property.active,
                deleted: property.deleted,
                website: property.website,
                content: contentWithMarkdown,
                ...(property.created_date && { created_date: property.created_date }),
                updated_date: Date.now(),
                ...(property.nano_id && { nano_id: property.nano_id }),
                ...(property.uid && { uid: property.uid })
            };

            console.log('[handlePublishConfirm] Request payload:', requestPayload);

            const allowedPeriods: Array<3 | 6 | 12> = [3, 6, 12];
            const period = (discountPeriod && allowedPeriods.includes(discountPeriod as 3 | 6 | 12)) ? discountPeriod : 3;
            const qs = new URLSearchParams();
            if (applyDiscount) {
                qs.append('discount', 'true');
                qs.append('period', String(period));
            }

            const endpoint = `admin/${apiVersion}/properties/${property.uid}/publish${qs.toString() ? `?${qs.toString()}` : ''}`;
            console.log(`[handlePublishConfirm] Making POST request to ${endpoint}`);

            const response = await apiJson<PropertyApiResponse>(endpoint, {
                method: 'POST',
                body: JSON.stringify(requestPayload)
            });

            console.log('[handlePublishConfirm] Publish response:', response);

            // Check if response contains a URL for redirect
            if (response.url) {
                console.log('[handlePublishConfirm] Received URL:', response.url);

                // Check if current user is the owner of the property
                const isPropertyOwner = currentUser && property.profile && currentUser.id === property.profile.id;
                console.log('[handlePublishConfirm] Is property owner:', isPropertyOwner);

                if (isPropertyOwner) {
                    // Redirect directly if user is the owner
                    console.log('[handlePublishConfirm] Redirecting directly to:', response.url);
                    setPublishStatus('redirecting');
                    setTimeout(() => {
                        window.location.href = response.url!;
                    }, 500);
                } else {
                    // Show modal with URL for non-owners
                    console.log('[handlePublishConfirm] Showing URL modal for non-owner');
                    setPublishUrl(response.url);
                    if (response.invoice_pdf) {
                        setPublishInvoicePdf(response.invoice_pdf);
                    }
                    setPublishUrlModalOpen(true);
                    setPublishStatus('success');
                    setTimeout(() => setPublishStatus('idle'), 3000);
                }
            } else {
                // Update local state with response (status should be updated)
                const publishedProperty = response as Property;
                setProperty(publishedProperty);
                console.log('[handlePublishConfirm] Property published successfully!', publishedProperty);

                setPublishStatus('success');
                setTimeout(() => setPublishStatus('idle'), 3000);
            }
        } catch (error) {
            console.error('Error publishing property:', error);
            setPublishStatus('error');
            setTimeout(() => setPublishStatus('idle'), 5000);
        }
    }, [property, applyDiscount, discountPeriod]);

    // Language management
    const handleLanguageChange = useCallback((_event: React.SyntheticEvent, newValue: string) => {
        setSelectedLanguage(newValue);
    }, []);

    const addLanguage = useCallback(
        (languageCode: string) => {
            if (!property.content[languageCode]) {
                setProperty((prev: Property) => {
                    // Get content from default_language to copy, with fallback to first available language or empty content
                    const defaultContent = prev.content[prev.default_language] ||
                        Object.values(prev.content)[0] || {
                            description: '',
                            location: '',
                            details: [],
                            faq: []
                        };

                    // Deep clone the default content to avoid reference sharing
                    const newContent = {
                        description: defaultContent.description,
                        location: defaultContent.location,
                        details: defaultContent.details.map(detail => ({
                            ...detail,
                            order: detail.order
                        })),
                        faq: defaultContent.faq.map(faq => ({
                            ...faq,
                            order: faq.order
                        }))
                    };

                    return {
                        ...prev,
                        content: {
                            ...prev.content,
                            [languageCode]: newContent
                        }
                    };
                });
            }
            setSelectedLanguage(languageCode);
            setNewLanguageDialogOpen(false);
            setNewLanguageCode('');
        },
        [property.content]
    );

    const removeLanguage = useCallback(
        (languageCode: string) => {
            if (Object.keys(property.content).length > 1) {
                const newContent = { ...property.content };
                delete newContent[languageCode];
                const remainingLanguages = Object.keys(newContent);

                setProperty((prev: Property) => ({
                    ...prev,
                    content: newContent,
                    default_language:
                        prev.default_language === languageCode ? remainingLanguages[0] : prev.default_language
                }));

                // Update selected language if we're removing the currently selected one
                if (selectedLanguage === languageCode) {
                    const newSelectedLanguage =
                        property.default_language === languageCode ? remainingLanguages[0] : property.default_language;
                    setSelectedLanguage(newSelectedLanguage);
                }
            }
        },
        [property.content, property.default_language, selectedLanguage]
    );

    // Location search
    const searchLocation = useCallback(
        async (query: string) => {
            if (!query.trim()) {
                setSearchResults([]);
                return;
            }

            if (!MAPBOX_TOKEN) {
                console.error('Mapbox access token is missing');
                setSearchResults([]);
                return;
            }

            try {
                const response = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=5${property.coordinates ? `&proximity=${property.coordinates.lon},${property.coordinates.lat}` : ''}`
                );
                const data = await response.json();

                if (data.features) {
                    setSearchResults(data.features);
                }
            } catch (error) {
                console.error('Error searching location:', error);
                setSearchResults([]);
            }
        },
        [MAPBOX_TOKEN, property.coordinates.lat, property.coordinates.lon]
    );

    const selectLocation = useCallback((feature: { place_name: string; center: [number, number] }) => {
        const [lng, lat] = feature.center;
        const address = feature.place_name;

        setProperty((prev: Property) => ({
            ...prev,
            coordinates: { lon: lng, lat: lat },
            address: address
        }));

        if (map.current && marker.current) {
            map.current.flyTo({ center: [lng, lat], zoom: 15 });
            marker.current.setLngLat([lng, lat]);
        }

        setSearchQuery(address);
        setSearchResults([]);
    }, []);

    // Photo management
    const handlePhotosDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setProperty(prev => {
            const oldIndex = prev.photos.findIndex((p: PropertyPhoto) => p.url === String(active.id));
            const newIndex = prev.photos.findIndex((p: PropertyPhoto) => p.url === String(over.id));
            if (oldIndex === -1 || newIndex === -1) return prev;
            const moved = arrayMove(prev.photos, oldIndex, newIndex);
            const mainCandidate = newIndex === 0 ? moved[0] : prev.photo;
            const { photos: orderedPhotos, main } = normalizePhotosWithMain(moved, mainCandidate);
            return { ...prev, photos: orderedPhotos, photo: main };
        });
    }, []);

    const setMainPhoto = useCallback((photo: PropertyPhoto) => {
        setProperty((prev: Property) => {
            const { photos: orderedPhotos, main } = normalizePhotosWithMain(prev.photos, photo);
            return { ...prev, photos: orderedPhotos, photo: main };
        });
    }, []);

    const removePhoto = useCallback((index: number) => {
        setProperty((prev: Property) => {
            const filtered = prev.photos.filter((_: PropertyPhoto, i: number) => i !== index);
            const { photos: orderedPhotos, main } = normalizePhotosWithMain(filtered, prev.photo);
            return { ...prev, photos: orderedPhotos, photo: main };
        });
    }, []);

    // Content management
    const updateContent = useCallback(
        (field: keyof PropertyContent, value: any) => {
            setProperty((prev: Property) => ({
                ...prev,
                content: {
                    ...prev.content,
                    [selectedLanguage]: {
                        ...prev.content[selectedLanguage],
                        [field]: value
                    }
                }
            }));
        },
        [selectedLanguage]
    );

    // Details management
    const addDetail = useCallback(() => {
        const newDetail: PropertyDetail = {
            name: 'New Detail',
            type: PropertyDetailsType.Info,
            size: PropertyDetailsSize.Small,
            order: property.content[selectedLanguage].details.length + 1,
            contents: ''
        };

        setProperty((prev: Property) => ({
            ...prev,
            content: {
                ...prev.content,
                [selectedLanguage]: {
                    ...prev.content[selectedLanguage],
                    details: [...prev.content[selectedLanguage].details, newDetail]
                }
            }
        }));
    }, [property.content, selectedLanguage]);

    const updateDetail = useCallback(
        (index: number, field: keyof PropertyDetail, value: any) => {
            setProperty((prev: Property) => ({
                ...prev,
                content: {
                    ...prev.content,
                    [selectedLanguage]: {
                        ...prev.content[selectedLanguage],
                        details: prev.content[selectedLanguage].details.map((detail: PropertyDetail, i: number) =>
                            i === index ? { ...detail, [field]: value } : detail
                        )
                    }
                }
            }));
        },
        [selectedLanguage]
    );

    const removeDetail = useCallback(
        (index: number) => {
            setProperty((prev: Property) => ({
                ...prev,
                content: {
                    ...prev.content,
                    [selectedLanguage]: {
                        ...prev.content[selectedLanguage],
                        details: prev.content[selectedLanguage].details.filter(
                            (_: PropertyDetail, i: number) => i !== index
                        )
                    }
                }
            }));
        },
        [selectedLanguage]
    );

    const handleDetailsDragEnd = useCallback(
        (event: DragEndEvent) => {
            setIsAnyDragging(false);
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            setProperty((prev: Property) => {
                const details = prev.content[selectedLanguage].details;
                const oldIndex = Number(active.id);
                const newIndex = Number(over.id);
                if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return prev;
                const newDetails = arrayMove(details, oldIndex, newIndex).map((d: PropertyDetail, idx: number) => ({
                    ...d,
                    order: idx + 1
                }));
                return {
                    ...prev,
                    content: {
                        ...prev.content,
                        [selectedLanguage]: {
                            ...prev.content[selectedLanguage],
                            details: newDetails
                        }
                    }
                };
            });
        },
        [selectedLanguage]
    );

    // FAQ management
    const addFAQ = useCallback(() => {
        const newFAQ: PropertyFAQ = {
            question: 'New Question',
            answer: '',
            order: property.content[selectedLanguage].faq.length + 1
        };

        setProperty((prev: Property) => ({
            ...prev,
            content: {
                ...prev.content,
                [selectedLanguage]: {
                    ...prev.content[selectedLanguage],
                    faq: [...prev.content[selectedLanguage].faq, newFAQ]
                }
            }
        }));
    }, [property.content, selectedLanguage]);

    const updateFAQ = useCallback(
        (index: number, field: keyof PropertyFAQ, value: any) => {
            setProperty((prev: Property) => ({
                ...prev,
                content: {
                    ...prev.content,
                    [selectedLanguage]: {
                        ...prev.content[selectedLanguage],
                        faq: prev.content[selectedLanguage].faq.map((faq: PropertyFAQ, i: number) =>
                            i === index ? { ...faq, [field]: value } : faq
                        )
                    }
                }
            }));
        },
        [selectedLanguage]
    );

    const removeFAQ = useCallback(
        (index: number) => {
            setProperty((prev: Property) => ({
                ...prev,
                content: {
                    ...prev.content,
                    [selectedLanguage]: {
                        ...prev.content[selectedLanguage],
                        faq: prev.content[selectedLanguage].faq.filter((_: PropertyFAQ, i: number) => i !== index)
                    }
                }
            }));
        },
        [selectedLanguage]
    );

    const handleFaqsDragEnd = useCallback(
        (event: DragEndEvent) => {
            setIsAnyDragging(false);
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            setProperty((prev: Property) => {
                const faqs = prev.content[selectedLanguage].faq;
                const oldIndex = Number(active.id);
                const newIndex = Number(over.id);
                if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return prev;
                const newFaqs = arrayMove(faqs, oldIndex, newIndex).map((f: PropertyFAQ, idx: number) => ({
                    ...f,
                    order: idx + 1
                }));
                return {
                    ...prev,
                    content: {
                        ...prev.content,
                        [selectedLanguage]: {
                            ...prev.content[selectedLanguage],
                            faq: newFaqs
                        }
                    }
                };
            });
        },
        [selectedLanguage]
    );

    // Property field updates
    const updatePropertyField = useCallback(<K extends keyof Property>(field: K, value: Property[K]) => {
        console.log('updatePropertyField called:', { field, value });
        setProperty((prev: Property) => ({ ...prev, [field]: value }));
    }, []);

    // Map coordinate updates
    const updateCoordinates = useCallback(
        async (coordinates: { lon: number; lat: number }) => {
            if (!MAPBOX_TOKEN) {
                setProperty((prev: Property) => ({
                    ...prev,
                    coordinates
                }));
                return;
            }

            try {
                const response = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${coordinates.lon},${coordinates.lat}.json?access_token=${MAPBOX_TOKEN}&limit=1`
                );
                const data = await response.json();
                const placeName = data?.features?.[0]?.place_name;

                setProperty((prev: Property) => ({
                    ...prev,
                    coordinates,
                    ...(placeName ? { address: placeName } : {})
                }));

                if (placeName) {
                    setSearchQuery(placeName);
                }
            } catch (error) {
                console.error('Error reverse geocoding coordinates:', error);
                setProperty((prev: Property) => ({
                    ...prev,
                    coordinates
                }));
            }
        },
        [MAPBOX_TOKEN]
    );

    return {
        // State
        property,
        hasFullAccess,
        selectedLanguage,
        searchQuery,
        searchResults,
        isLoading,
        saveStatus,
        publishStatus,
        publishUrl,
        publishInvoicePdf,
        publishUrlModalOpen,
        publishConfirmModalOpen,
        uploadDialogOpen,
        newLanguageDialogOpen,
        newLanguageCode,
        isAnyDragging,
        map,
        marker,
        currentUser,
        applyDiscount,
        discountPeriod,

        // Actions
        setProperty,
        setSelectedLanguage,
        setSearchQuery,
        setUploadDialogOpen,
        setPublishUrlModalOpen,
        setPublishConfirmModalOpen,
        setApplyDiscount,
        setDiscountPeriod,
        setNewLanguageDialogOpen,
        setNewLanguageCode,
        setIsAnyDragging,

        // Business logic
        loadProperty,
        handleSave,
        handlePublish,
        handlePublishConfirm,
        handleLanguageChange,
        addLanguage,
        removeLanguage,
        searchLocation,
        selectLocation,
        handlePhotosDragEnd,
        setMainPhoto,
        removePhoto,
        updateContent,
        addDetail,
        updateDetail,
        removeDetail,
        handleDetailsDragEnd,
        addFAQ,
        updateFAQ,
        removeFAQ,
        handleFaqsDragEnd,
        updatePropertyField,
        updateCoordinates,

        // Utilities
        htmlToMarkdown,
        markdownToHtml
    };
};
